import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';

import type {
  Direction,
  WithInherit,
  PhysicalAlignment,
  LogicalAlignment,
} from '@wix/editor-elements-common-utils';
import {
  getLabelPadding,
  withFallbackValues,
  castToInheritIfNeeded,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type {
  SelectionTagsListDefinition,
  ISelectionTagsListMapperProps,
  SelectionTagsListCSSVars,
  SelectionTagsListCssCarmiData,
} from '../SelectionTagsList.types';
import { physicalToLogicalAlignment } from '../utils';
import { convertPhysicalInputAlignmentToLogical } from '@wix/editor-elements-common-utils/src/commons/inputUtils';

export const props = withCompInfo<
  ISelectionTagsListMapperProps,
  SelectionTagsListDefinition
>()(['compData', 'compProps'], ({ compData, compProps }) => {
  const { isDisabled, required, label } = compProps;
  const { options, value } = compData;
  return { options, value, isDisabled, required, label };
});

export const css = withCompInfo<
  SelectionTagsListCSSVars,
  SelectionTagsListDefinition,
  SelectionTagsListCssCarmiData
>()(
  [
    'compData',
    'compProps',
    'compLayout',
    'hasResponsiveLayout',
    'isOneDocMigrated',
    'styleProperties',
  ],
  (
    {
      styleProperties,
      compData,
      compProps,
      compLayout,
      hasResponsiveLayout,
      isOneDocMigrated,
    },
    carmiProps,
  ) => {
    const { labelMargin, hasAutoHeight } = compProps;

    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      inputDirection = 'inherit',
    } = compData;
    const { align, labelAlign, inputAlign = 'inherit' } = styleProperties;

    const heightAutoCss =
      hasAutoHeight || !isOneDocMigrated ? { height: 'auto' as const } : {};

    const minHeightCss =
      hasResponsiveLayout || hasAutoHeight || isOneDocMigrated
        ? {}
        : {
            'min-height': `${compLayout.height}px`,
          };

    return {
      '--textAlign': labelAlign as LogicalAlignment,
      '--labelDir': labelDirection as WithInherit<Direction>,
      '--direction': withSiteDirection(direction as WithInherit<Direction>),
      '--alignment': align as LogicalAlignment,
      '--inputAlign': inputAlign,
      '--inputDirection': inputDirection,
      '--fnt': carmiProps['--fnt'],
      '--labelPadding': getLabelPadding(compProps),
      '--labelMarginBottom': `${labelMargin}px`,
      ...heightAutoCss,
      ...minHeightCss,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'direction', target: 'direction' }],
        enhancer: direction => castToInheritIfNeeded(direction, 'ltr'),
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'align' }],
        enhancer: (alignment: PhysicalAlignment, { compProps }) => {
          const logicalAlign = physicalToLogicalAlignment(
            alignment,
            compProps.direction,
          );
          return logicalAlign;
        },
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'alignment', target: 'labelDirection' }],
        enhancer: (alignment: PhysicalAlignment) => {
          const labelDirection = alignment === 'right' ? 'rtl' : 'ltr';
          return labelDirection;
        },
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'labelAlign' }],
        enhancer: (alignment: PhysicalAlignment) => {
          const labelAlign = convertPhysicalInputAlignmentToLogical(alignment);
          return labelAlign;
        },
      },
    ]),
  ],
);
export const stateRefs = withStateRefsValues(['experiments']);

export default createComponentMapperModel({
  props,
  css: css as any,
  stateRefs,
});
